import { Accordion, AccordionDetails, AccordionSummary, Box, type BoxProps, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { OrderListDishTypeItem } from "components/orderSteps/components/OrderListDishTypeItem";
import { DishTypeImage } from "components/primitives/DishTypeImage";
import { useMixpanelTrackers } from "hooks/useMixpanelTrackers";
import { useAppSelector } from "hooks/utilsHooks/storeHooks";
import { memo, type RefObject, useEffect, useRef } from "react";
import { selectSelectedLanguage } from "slices/appStateSlice";
import { selectOrderState } from "slices/transactionSlice";
import { type Maybe, type Price, TransactionState } from "types";
import { DishItemDisplayType, type LocalDishType } from "types/mainTypes";
import { createSplitDishName } from "utils/utils";
import { DishTypeDetailTabs } from "./components/DishTypeDetailTabs";
import { InfoRow } from "./components/InfoRow";

type DishTypeItemProps = {
	dishType: LocalDishType;
	type: DishItemDisplayType;
	pieces?: number;
	isOpen?: boolean;
	// Takes the dish-type id so callers can pass a single stable handler (e.g. useMealListEvents'
	// handleOpen) instead of a per-item closure that would defeat React.memo.
	onOpen?: (dishTypeId: string, ref: RefObject<HTMLDivElement | null>) => void;
	notFiltered?: boolean;
	priceOverride?: Maybe<Price>;
};

const DishTypeItemComponent = ({
	dishType,
	type,
	pieces,
	isOpen = false,
	onOpen,
	notFiltered = false,
}: DishTypeItemProps) => {
	const { description, image } = dishType;
	const selectedLanguage = useAppSelector(selectSelectedLanguage);
	const orderState = useAppSelector(selectOrderState);

	const name = createSplitDishName(description?.[selectedLanguage]?.name);
	const { trackOpenDishItemDetail } = useMixpanelTrackers();

	const ref = useRef<HTMLDivElement>(null);

	const handleAccordionOpen = (e: React.SyntheticEvent<Element, Event>) => {
		if (onOpen && e && type !== DishItemDisplayType.ORDER) {
			trackOpenDishItemDetail(isOpen, dishType, name);
			onOpen(dishType.id, ref);
		}
	};

	useEffect(() => {
		if (isOpen && onOpen && orderState === TransactionState.Finished) {
			onOpen(dishType.id, ref);
		}
	}, [orderState, isOpen, onOpen, dishType.id]);

	if (type === DishItemDisplayType.LIST) {
		return (
			<StyledAccordion
				key={`accordion_${dishType.id}`}
				onChange={(e) => {
					handleAccordionOpen(e);
				}}
				expanded={isOpen}
				slotProps={{
					transition: { unmountOnExit: true, timeout: 200 },
				}}
				ref={ref}
			>
				<StyledAccordionSummary className={isOpen ? "expanded" : ""}>
					<DishTypeImage notFiltered={notFiltered} imageUrl={image?.url} className={isOpen ? "expanded" : ""} />
					<HeaderWrapper className={isOpen ? "expanded" : ""}>
						<Header className={isOpen ? "expanded" : ""}>{name?.header}</Header>
						<Subheader expanded={isOpen}>{name?.subHeader || <>&nbsp;</>}</Subheader>
						<InfoRow dishType={dishType} type={type} pieces={pieces} />
					</HeaderWrapper>
				</StyledAccordionSummary>
				<StyledAccordionDetails>
					<DishTypeDetailTabs dishType={dishType} />
				</StyledAccordionDetails>
			</StyledAccordion>
		);
	}
	return <OrderListDishTypeItem name={name} dishType={dishType} pieces={pieces} />;
};

// Memoised so a parent re-render (MealList re-renders on every subscription frame) does not re-render
// every dish item. Relies on stable props — notably `onOpen` must be a stable reference (pass
// useMealListEvents' handleOpen directly, not a per-item closure).
export const DishTypeItem = memo(DishTypeItemComponent);

const StyledAccordionSummary = styled(AccordionSummary)(() => ({
	padding: "0px 20px 0 0",
	alignItems: "flex-start",
	transition: "all 200ms linear",
	"&.expanded": {
		minHeight: "240px",
	},
}));

const Header = styled(Typography)(({ theme }) => ({
	color: theme.palette.common.white,
	fontWeight: 600,
	fontSize: "1.75rem",
	lineHeight: "40px",

	"&.expanded": {
		fontSize: "2.25rem",
		transform: "font",
		transition: "all 200ms linear",
	},
}));

const Subheader = styled(Typography, {
	shouldForwardProp: (prop) => prop !== "expanded",
})<BoxProps & { expanded: boolean }>(({ theme, ...props }) => ({
	color: theme.palette.common.white,
	fontWeight: 500,
	fontSize: "1.25rem",
	lineHeight: "1.75rem",
	...(!props.expanded && {
		whiteSpace: "nowrap",
		textOverflow: "ellipsis",
		width: "100%",
		overflow: "hidden",
	}),
	// Set min height to keep the wrapper proportions when there is no subtitle
	minHeight: "28px",
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
	width: "100%",
	borderRadius: theme.shape.borderRadius,
	backgroundColor: "#353535",
	transition: "all 200ms linear",

	"&.MuiAccordion-root:before": {
		display: "none",
	},
	"& .MuiAccordionSummary-content": {
		width: "100%",
		margin: 0,
		transition: "all 200ms linear",
	},

	"& .MuiAccordionSummary-root": {
		transition: "all 200ms linear",
	},

	"& .MuiCollapse-root": {
		overflow: "hidden",
		transition: "all 200ms linear",
	},
}));

const HeaderWrapper = styled(Box)(() => ({
	display: "flex",
	flexDirection: "column",
	width: "calc(100% - 120px)",
	marginLeft: "auto",
	transition: "all 200ms linear",
	padding: "20px 0",

	"&.expanded": {
		padding: "25px 0",
		width: "calc(100% - 180px)",
	},
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
	padding: "8px 20px 40px",
	color: theme.palette.common.white,

	"&.MuiAccordionDetails-root": {
		marginTop: 0,
	},
}));
